import React, { useState, useEffect } from 'react';

import WpIcon from '../icon/WpIcon';

import Colors from '../../colors/ColorConstants';

import './WpDataTable.scss';

export enum SortDirection {
  ASC,
  DESC,
}

type Props = {
  columns: Array<{
    name: string;
    columnGroup?: string;
    selector?: string;
    noWrap?: boolean;
    initialSort?: SortDirection;
    component?: Function;
  }>;
  rows: Array<any>;
  enableSort?: boolean;
};

type Sort = {
  direction: SortDirection;
  columnIndex: number;
};

const initialSort = columns => {
  const columnIndex = columns.findIndex(c => c.initialSort !== undefined);

  if (columns[columnIndex]) {
    return {
      direction: columns[columnIndex].initialSort,
      columnIndex,
    };
  } else {
    return null;
  }
};

const WpDataTable = ({ columns, rows, enableSort = true }: Props) => {
  const [sortedRows, setSortedRows] = useState([...rows]);
  const [sort, setSort] = useState<Sort | null>(initialSort(columns));

  useEffect(() => {
    if (sort) {
      const { direction, columnIndex } = sort;
      const newSortedRows = [...rows].sort((a, b) => {
        const selector = columns[columnIndex].selector;
        const [first, second] = direction == SortDirection.ASC ? [a, b] : [b, a];

        if (first[selector] < second[selector]) {
          return -1;
        } else if (first[selector] > second[selector]) {
          return 1;
        } else {
          return 0;
        }
      });

      setSortedRows(newSortedRows);
    } else {
      // If there is no sort, revert back to the original ordering from the rows prop
      setSortedRows(rows);
    }
  }, [sort, rows]);

  const handleColumnHeaderClick = columnIndex => {
    if (!enableSort) return false;

    if (sort?.direction == SortDirection.DESC && sort?.columnIndex == columnIndex) {
      setSort(null);
    } else {
      const direction =
        sort?.direction == SortDirection.ASC && sort?.columnIndex == columnIndex
          ? SortDirection.DESC
          : SortDirection.ASC;

      setSort({ columnIndex, direction });
    }
  };

  const CustomCellMarkup = ({ component, row, selector }) => {
    const Row = component;
    return <Row row={row} selector={selector} />;
  };

  const baseClass = 'WpDataTable';

  return (
    <table className={baseClass}>
      <thead>
        <tr>
          {columns.map(({ name, columnGroup }, index) => {
            // Detect the first column of a group that is where the group header will live as well as ensuring
            // the cell has a left border to separate from other groups.
            const isFirstOfGroup = columns.findIndex(c => c.columnGroup === columnGroup) === index;
            const modifier = isFirstOfGroup ? 'first-of-group' : 'group';
            const className = columnGroup ? `${baseClass}__header-column--${modifier}` : '';
            let iconName = 'sort';
            if (sort?.columnIndex == index) {
              iconName = sort.direction == SortDirection.DESC ? 'sort-up' : 'sort-down';
            }

            return (
              <th
                className={`${baseClass}__header-column ${className}`}
                key={index}
                onClick={() => handleColumnHeaderClick(index)}
              >
                {columnGroup && isFirstOfGroup && (
                  <div className={`${baseClass}__header-column__group`}>{columnGroup}</div>
                )}
                <span className={`${baseClass}__header-column__name`}>{name}</span>
                {enableSort ? <WpIcon name={iconName} color={Colors.gray[700]} /> : null}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((row, i) => (
          <tr className={`${baseClass}__body-row`} key={i}>
            {columns.map(({ selector, noWrap, component }, j) => {
              const additionalClasses = noWrap ? '${baseClass}__body-cell--nowrap' : '';
              return (
                <td className={`${baseClass}__body-cell ${additionalClasses}`} key={j}>
                  {component ? <CustomCellMarkup component={component} row={row} selector={selector} /> : row[selector]}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default WpDataTable;
